import { VITE_SOLANA_VALIDATOR_ADDRESS } from '@/env'
import { formatNumber, getSolanaExplorerAddressLink } from '@/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import { CopyIcon, SquareArrowOutUpRight } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useValidatorAPYContext } from '../providers/ValidatorAPYContext'

const formatSol = (value: number): string => formatNumber(value, 4)

export const ValidatorInfo = () => {
  const { t } = useLingui()
  const validatorAddress = VITE_SOLANA_VALIDATOR_ADDRESS
  const { totalStakeLamports, commission, apy, isLoading: isValidatorAPYLoading } = useValidatorAPYContext()

  const totalStakeSol = totalStakeLamports / LAMPORTS_PER_SOL

  const explorerLink = getSolanaExplorerAddressLink(validatorAddress)

  const truncateAddress = (address: string) => {
    if (address.length <= 16) return address
    return `${address.slice(0, 6)}...${address.slice(-6)}`
  }

  const [copied, setCopied] = useState(false)
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(validatorAddress)
      setCopied(true)
      setTimeout(() => setCopied(false), 500)
    } catch {}
  }, [validatorAddress])

  return (
    <div className="rounded-xl border border-gray-100 p-6">
      <div className="flex flex-col gap-6 pb-6">
        <div className="grid grid-cols-2">
          <div className="flex grow flex-col gap-2">
            <span className="text-body-3 text-gray-90">
              <Trans>Expected APY</Trans>
            </span>
            <span className="text-gray-70 font-semibold">{isValidatorAPYLoading ? '-' : `${apy}%`}</span>
          </div>
          <div className="flex grow flex-col gap-2 border-l border-gray-100 pl-5">
            <span className="text-body-3 text-gray-90">
              <Trans>Commission</Trans>
            </span>
            <span className="text-gray-70 font-semibold">{isValidatorAPYLoading ? '-' : `${commission}%`}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 border-t border-gray-100 py-6">
        <span className="text-body-3 text-gray-90">
          <Trans>Total Stake</Trans>
        </span>
        <span className="text-gray-70 font-semibold">
          {isValidatorAPYLoading ? '-' : `${formatSol(totalStakeSol)} SOL`}
        </span>
      </div>

      <div className="flex flex-col gap-2 border-y border-gray-100 py-6">
        <span className="text-body-3 text-gray-90">
          <Trans>Staking address</Trans>
        </span>
        <div className="text-gray-70 flex items-center gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className="text-gray-70 hover:text-green flex shrink-0 gap-2 rounded p-1 transition-colors"
            title={copied ? t`Copied!` : t`Copy address`}
            aria-label={copied ? t`Copied` : t`Copy address`}
          >
            <span className="text-default break-all">{truncateAddress(validatorAddress)}</span>
            {copied ? (
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <CopyIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
      <div className="pt-6">
        <a
          href={explorerLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-70 hover:text-green flex items-center gap-2 underline transition-colors"
        >
          <Trans>View on Block Explorer</Trans>
          <SquareArrowOutUpRight size={18} />
        </a>
      </div>
    </div>
  )
}
